<template>
    <div class="world-map-container">
        <p v-if="shouldDisplayGrid" v-for="gridInfoItem in gridInfo" class="grid-info-text" :style="{
            'left': gridInfoItem.left,
            'top': gridInfoItem.top
        }">{{gridInfoItem.text}}</p>

        <svg id="svg" ref="svg" width="100%" height="100%" style="background-color:#1A2127">
            <g>
                <rect class="square" v-for="squareKey in Object.keys(squares)" v-bind:key="squareKey"
                      style="z-index:999"
                      @mouseover="onSquareMouseOver"
                      @mouseleave="onSquareMouseOut"
                      :data-id="squareKey"
                      :width="squareWidthPercentage + '%'"
                      :height="squareHeightPercentage + '%'"
                      :style="{'fill': squares[squareKey].color}"
                      :x="getX(squares[squareKey].x)"
                      :y="getY(squares[squareKey].y)"></rect>
            </g>

            <line v-if="shouldDisplayGrid"
                  v-for="line in lines"
                  v-bind:key="line.x1 + '-' + line.y1 + '-' + line.x2 + '-' + line.y2"
                  :x1="getPercentStr(line.x1)"
                  :y1="getPercentStr(line.y1)"
                  :x2="getPercentStr(line.x2)"
                  :y2="getPercentStr(line.y2)"
                  :style="{'stroke': getLineColor(line.size), 'stroke-width': getPercentStr(getLineWidth(line.type, line.size))}"></line>

            <!-- Highlight square rect -->
            <rect v-if="hoveredRect != null && shouldDisplayGrid" class="highlight-square-rect"
                  :x="getX(hoveredRect.x)"
                  y="5.9%"
                  :width="getPercentStr(squareWidthPercentage)"
                  height="88.2%">
            </rect>

            <!-- Highlight square rect -->
            <rect v-if="hoveredRect != null && shouldDisplayGrid" class="highlight-square-rect"
                  x="3.0729%"
                  :y="getY(hoveredRect.y)"
                  width="93.75%"
                  :height="getPercentStr(squareHeightPercentage)">
            </rect>

            <!-- Section rect -->
            <rect
                v-if="sectionRect != null && shouldShowSectionRect(getSectionRectName(sectionRect)) && shouldDisplayGrid"
                width="22.0584%"
                height="17.6%"
                :style="{'stroke': thickLineColor, 'stroke-width' : 2, 'fill-opacity' : 0.2, 'pointer-events': 'none' }"
                :x="getPercentStr(sectionRect.leftBound.value)"
                :y="getPercentStr(sectionRect.topBound.value)">

            </rect>

            <pulse-rect v-for="pulseRect in pulseRects" v-bind:key="pulseRect.id"
                        :id="pulseRect.id"
                        :rect-width-percentage="getPercentStr(squareWidthPercentage)"
                        :rect-height-percentage="getPercentStr(squareHeightPercentage)"
                        :color="pulseRect.color"
                        :x="getX(pulseRect.x)"
                        :y="getY(pulseRect.y)"
                        v-on:animationOver="pulseAnimationOver">
            </pulse-rect>
        </svg>

        <!-- Section name text -->
        <p
            v-if="sectionRect != null && shouldShowSectionRect(getSectionRectName(sectionRect)) && shouldDisplayGrid"
            :style="{
                'font-family' : 'Digital-Serial-Regular',
                'font-size': '3.5vw',
                'color' : thickLineColor,
                'position' : 'absolute',
                'left' : getPercentStr(sectionRect.leftBound.value + 0.5),
                'top' : getPercentStr(sectionRect.topBound.value),
                'margin': 0,
                'pointer-events': 'none'
            }">{{getSectionRectName(sectionRect)}}</p>

        <p
            v-if="sectionRect != null && shouldShowSectionRect(getSectionRectName(sectionRect)) && shouldDisplayGrid"
            :style="{
                'font-family' : 'Digital-Serial-Regular',
                'font-size': '0.8vw',
                'color' : thickLineColor,
                'position' : 'absolute',
                'left' : getPercentStr(sectionRect.leftBound.value + 0.5),
                'top' : getPercentStr(sectionRect.topBound.value + 15),
                'margin': 0,
                'pointer-events': 'none'
            }">{{"Threat level : " + getSectionThreatLevel(getSectionRectName(sectionRect))}}</p>

    </div>
</template>

<script>

    import mapData from '@/assets/map-data.json'
    import lineData from '@/assets/line-data.json'
    import gridBounds from '@/assets/grid-bounds.json'

    import PulseRect from '@/components/world-map/PulseRect.vue'
    import Common from '@/utils/Common.vue'

    export default {
        name: "world-map",
        props: {
            feed: {
                type: Array,
                required: true
            },
            shouldDisplayGrid: {
                type: Boolean,
                required: true
            },
            newFeedDisplayInterval: {
                type: Number,
                required: true
            }
        },
        mixins: [Common],
        data() {
            return {
                draw: null,
                squares: {},
                animationSquares: {},
                lines: [],
                gridInfo: [],
                pulseRects: [],

                squareWidthPercentage: 0.3125,
                squareHeightPercentage: 0.6,

                spaceWidthPercentage: 0.1041,
                spaceHeightPercentage: 0.2,

                lineWidthPercentage: 0.05,
                lineHeightPercentage: 0.05,

                horizontalMargin: 3.0729,
                verticalMargin: 5.9,

                lineColor: "#1F3635",
                thickLineColor: "rgba(0, 255, 169, 1)",

                hoveredRect: null,
                sectionRect: null,

                lastSquareMouseInTimestamp: 0,
                previousRectColor: null,

                squareColor: "rgba(41, 56, 70, 1)",
                virusColor: "rgba(255, 0, 67, 1)",
                virusBlurColor: "#611D39",
                trojanColor: "rgba(118, 0, 255, 1)",
                trojanBlurColor: "#29234B",

                interval: null,

                totalHighlightedSquareCount: 0,
                totalFileCount: 0,
                totalTrojanCount: 0,
                totalVirusCount: 0,
                sectionStats: {}
            }
        },
        components: {
            'pulse-rect': PulseRect
        },
        methods: {
            getX(x) {
                return (x * this.squareWidthPercentage + x * this.spaceWidthPercentage + this.horizontalMargin) + "%";
            },
            getY(y) {
                return (y * this.squareHeightPercentage + y * this.spaceHeightPercentage + this.verticalMargin) + "%";
            },
            createSquareId(x, y) {
                return x + "-" + y;
            },
            getLineColor(size) {
                if (size == "small")
                    return this.thickLineColor;
                return this.lineColor;
            },
            getPercentStr(value) {
                return value + "%";
            },
            getValueFromPercentStr(str) {
                return parseFloat(str.substr(0, str.length - 1));
            },
            getLineWidth(type, size) {
                var width;
                if (type == "vertical")
                    width = this.lineWidthPercentage;
                else
                    width = this.lineHeightPercentage;

                if (size == "small")
                    width = width * 3;

                return width;
            },
            getCoordinatesFromGeoLocation(lat, lon) {
                var svgBox = this.$refs.svg.getBBox();
                var width = svgBox.width;
                var height = svgBox.height;

                var y = ((-1 * lat) + 90) * (height / 130);
                var x = (lon + 180) * (width / 350);

                return {x: x, y: y};
            },
            getDistanceBetweenPoints(x1, y1, x2, y2) {
                var a = x1 - x2;
                var b = y1 - y2;
                return Math.sqrt(a * a + b * b);
            },
            findClosestRegionBounds: function (x, y) {
                // Find closest region by looking left and top of the point
                var xDistance = 99999;
                var yDistance = 99999;
                var leftBound;
                var topBound;
                var rightBound;
                var bottomBound;

                var originX = x + (this.squareWidthPercentage / 2);
                var originY = y + (this.squareHeightPercentage / 2);

                for (let i = 0; i < gridBounds.length; i++) {
                    let bound = gridBounds[i];
                    if (bound.type === "horizontal") {
                        let diff = originX - bound.value;
                        if (diff >= 0 && diff < xDistance) {
                            xDistance = diff;
                            leftBound = bound;
                            rightBound = gridBounds[i + 1];
                        }
                    }
                    else {
                        let diff = originY - bound.value;
                        if (diff > 0 && diff < yDistance) {
                            yDistance = diff;
                            topBound = bound;
                            bottomBound = gridBounds[i + 1];
                        }
                    }
                }

                return {
                    leftBound: leftBound,
                    topBound: topBound,
                    rightBound: rightBound,
                    bottomBound: bottomBound
                }
            },
            findClosestSquare(x, y) {
                var minDistance = 99999;
                var closestSquare;
                var squares = this.squareElements;
                for (let i = 0; i < squares.length; i++) {
                    let square = squares[i];
                    let squareBox = square.getBBox();
                    let squareOriginX = squareBox.x + (squareBox.width / 2);
                    let squareOriginY = squareBox.y + (squareBox.height / 2);
                    let distance = this.getDistanceBetweenPoints(x, y, squareOriginX, squareOriginY);
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestSquare = square;
                    }
                }
                var id = closestSquare.getAttribute("data-id");
                return this.squares[id];
            },
            onSquareMouseOver(event) {
                if (!this.shouldDisplayGrid) return;
                this.lastSquareMouseInTimestamp = Date.now()

                var element = event.target;
                var id = element.getAttribute("data-id");
                var x = element.getAttribute("x");
                var y = element.getAttribute("y");

                this.sectionRect = this.findClosestRegionBounds(this.getValueFromPercentStr(x), this.getValueFromPercentStr(y));

                var square = this.squares[id];
                this.previousRectColor = square.color;
                square.color = "white";

                this.hoveredRect = square;
            },
            onSquareMouseOut(event) {
                if (!this.shouldDisplayGrid) return;
                var element = event.target;
                var id = element.getAttribute("data-id");
                var square = this.squares[id];

                square.color = this.previousRectColor;
                this.hoveredRect = null;

                var self = this;
                var time = Date.now();
                setTimeout(function () {
                    if (self.lastSquareMouseInTimestamp < time)
                        self.sectionRect = null;
                }, 100);
            },
            periodicallyDisplayNewFeedData() {
                var self = this;

                function getRandomMilisecond() {
                    var number = self.getRandomInRange(0, 200, 0);
                    if (number % 2 == 0) {
                        return number;
                    }
                    else {
                        return -1 * number;
                    }
                }

                function intervalFunction() {
                    // Retrieve a new log from backlog
                    let newFeedData = self.feed.shift();

                    if (newFeedData) {
                        // Display it in both the map and table
                        let longitude = newFeedData.geoPoint.longitude;
                        let latitude = newFeedData.geoPoint.latitude;

                        var coordinates = self.getCoordinatesFromGeoLocation(parseFloat(latitude), parseFloat(longitude));
                        let square = self.findClosestSquare(coordinates.x, coordinates.y);

                        if (square.color == self.squareColor) {
                            self.totalHighlightedSquareCount++;
                        }

                        square.color = Date.now() % 2 == 0 ? self.virusColor : self.trojanColor;
                        self.addPulseRect(square);
                        self.createBlurAroundTheSquare(square);
                        self.addStats(square);
                        self.updateThreatWatch();
                    }

                    clearInterval(self.displayNewFeedDataInterval);
                    self.displayNewFeedDataInterval = setInterval(intervalFunction, self.newFeedDisplayInterval + getRandomMilisecond());
                }

                this.displayNewFeedDataInterval = setInterval(intervalFunction, this.newFeedDisplayInterval + getRandomMilisecond());
            },
            addStats(square) {
                try {
                    this.totalFileCount++;
                    if (square.color == this.virusColor) {
                        this.totalVirusCount++;
                    } else {
                        this.totalTrojanCount++;
                    }

                    var x = this.getX(square.x);
                    var y = this.getY(square.y);
                    var sectionRect = this.findClosestRegionBounds(this.getValueFromPercentStr(x), this.getValueFromPercentStr(y));
                    var sectionRectName = this.getSectionRectName(sectionRect);

                    if (this.sectionStats[sectionRectName]) {
                        if (square.color == this.virusColor) {
                            this.sectionStats[sectionRectName].virus = this.sectionStats[sectionRectName].virus + 1;
                        } else {
                            this.sectionStats[sectionRectName].trojan = this.sectionStats[sectionRectName].trojan + 1;
                        }
                    } else {
                        if (square.color == this.virusColor) {
                            this.sectionStats[sectionRectName] = {
                                virus: 1,
                                trojan: 0
                            };
                        } else {
                            this.sectionStats[sectionRectName] = {
                                virus: 0,
                                trojan: 1
                            };
                        }
                    }

                    for (let sectionRectName in this.sectionStats) {
                        var sectionTotal = this.sectionStats[sectionRectName].virus + this.sectionStats[sectionRectName].trojan;
                        this.sectionStats[sectionRectName].threatLevel = this.getPercentStr(((sectionTotal * 100) / this.totalFileCount).toFixed(2));
                    }
                } catch (e) {
                    // console.log(e.stack);
                }
            },
            getLetterFromNumber(number) {
                var alphabet = 'abcdefghijklmnopqrstuvwxyz';
                return alphabet.charAt(number).toUpperCase();
            },
            addPulseRect(square) {
                if (this.isSafari || this.isFirefox) {
                    return;
                }

                var id = "" + Date.now();
                this.pulseRects.push({
                    id: id,
                    x: square.x,
                    y: square.y,
                    color: square.color
                });
            },
            pulseAnimationOver(id) {
                var index = -1;
                for (let i = 0; i < this.pulseRects.length; i++) {
                    let pulseRect = this.pulseRects[i];
                    if (pulseRect.id === id) {
                        index = i;
                        break;
                    }
                }

                if (index !== -1) {
                    this.pulseRects.splice(index, 1);
                }

            },
            createBlurAroundTheSquare(square) {
                var squaresAround = [];
                var availableRandomSquares = [];
                var x = square.x;
                var y = square.y;

                squaresAround.push(this.squares[this.createSquareId(x - 1, y - 1)]);
                squaresAround.push(this.squares[this.createSquareId(x - 1, y)]);
                squaresAround.push(this.squares[this.createSquareId(x - 1, y + 1)]);
                squaresAround.push(this.squares[this.createSquareId(x, y - 1)]);
                squaresAround.push(this.squares[this.createSquareId(x, y + 1)]);
                squaresAround.push(this.squares[this.createSquareId(x + 1, y - 1)]);
                squaresAround.push(this.squares[this.createSquareId(x + 1, y)]);
                squaresAround.push(this.squares[this.createSquareId(x + 1, y + 1)]);

                for (let i = 0; i < squaresAround.length; i++) {
                    let squareAround = squaresAround[i];
                    if (squareAround && squareAround.color === this.squareColor) {
                        availableRandomSquares.push(squareAround);
                    }
                }

                if (availableRandomSquares.length > 0) {
                    this.shuffle(availableRandomSquares);
                    var randomSquareCount = this.getRandomNumber(availableRandomSquares.length > 4 ? 4 : availableRandomSquares.length);
                    if (randomSquareCount == 0)
                        randomSquareCount = 1;

                    for (let i = 0; i < randomSquareCount; i++) {
                        let randomSquare = availableRandomSquares[i];

                        if (square.color === this.virusColor) {
                            randomSquare.color = this.virusBlurColor;
                        } else if (square.color === this.trojanColor) {
                            randomSquare.color = this.trojanBlurColor;
                        }
                    }
                }
            },
            getSectionRectName(sectionRect) {
                return sectionRect.leftBound.section + sectionRect.topBound.section;
            },
            getSectionThreatLevel(sectionRectName) {
                return this.sectionStats[sectionRectName] ? this.sectionStats[sectionRectName].threatLevel : "0%";
            },
            shouldShowSectionRect(sectionRectName) {
                return sectionRectName != 'A1' && sectionRectName != "E1" && sectionRectName != "E2" && sectionRectName != "E5" && sectionRectName != "A5" && sectionRectName != "A4" && sectionRectName != "C5";
            },
            fillGridInfo() {
                var grid = [];
                for (let i = 0; i < 5; i++) {
                    grid.push({
                        text: i + 1,
                        left: 'calc(' + this.getPercentStr(3.0729) + " - 26px",
                        top: 'calc(' + this.getPercentStr(23.7 + (i * 17.6)) + " - 26px"
                    })
                }
                for (let i = 0; i < 3; i++) {
                    let left;
                    if (i == 0) left = 39.0219;
                    if (i == 1) left = 61.0803;
                    if (i == 2) left = 83.1387;
                    grid.push({
                        text: this.getLetterFromNumber(i),
                        left: 'calc(' + this.getPercentStr(left) + ' - 24px',
                        top: 'calc(' + this.getPercentStr(5.9) + ' - 26px'
                    })
                }
                this.gridInfo = grid;
            },
            updateThreatWatch() {
                let trojanPercent = 0;
                let virusPercent = 0;
                let avgThreatLevel = 0;

                try {
                    if (this.sectionRect) {
                        var sectionRectName = this.getSectionRectName(this.sectionRect);
                        var sectionStats = this.sectionStats[sectionRectName];
                        var totalInSection = sectionStats.virus + sectionStats.trojan;

                        avgThreatLevel = this.getValueFromPercentStr(this.getSectionThreatLevel(sectionRectName));
                        trojanPercent = (sectionStats.trojan * 100) / totalInSection;
                        virusPercent = (sectionStats.virus * 100) / totalInSection;
                    } else {
                        let a2Level = this.getValueFromPercentStr(this.getSectionThreatLevel("A2"));
                        let b2Level = this.getValueFromPercentStr(this.getSectionThreatLevel("A2"));
                        let c2Level = this.getValueFromPercentStr(this.getSectionThreatLevel("A2"));
                        let avgLevel = (a2Level + b2Level + c2Level) / 3;
                        trojanPercent = (this.totalTrojanCount * 100) / this.totalFileCount;
                        virusPercent = (this.totalVirusCount * 100) / this.totalFileCount;
                        avgThreatLevel = avgLevel.toFixed(2);
                    }
                } catch (e) {
                    // console.log(e.stack);
                }

                var data = {
                    trojanPercent: trojanPercent,
                    virusPercent: virusPercent,
                    avgThreatLevel: avgThreatLevel
                };
                this.$emit("updateThreatWatch", data);
            },
            createMap() {
                var squares = {};
                for (var i = 0; i < mapData.length; i++) {
                    let id = this.createSquareId(mapData[i].x, mapData[i].y);
                    mapData[i].color = this.squareColor;
                    squares[id] = mapData[i];
                }
                this.squares = squares;
            },
            createLines() {
                this.lines = lineData;
            }
        },
        mounted() {
            this.createMap();
            this.createLines();
            this.fillGridInfo();
            this.periodicallyDisplayNewFeedData();

            this.squareElements = document.getElementsByClassName("square");
        },
        beforeDestroy() {
            clearInterval(this.displayNewFeedDataInterval);
        }
    }

</script>

<style scoped>

    .world-map-container {
        width: 100%;
        height: 100%;
        min-width: 1280px !important;
        min-height: 720px !important;
        overflow: hidden;
        position: relative;
    }

    .square {
        pointer-events: all;
    }

    .grid-info-text {
        font-family: 'Digital-Serial-Bold';
        font-size: 0.875em;
        font-weight: bold;
        color: rgba(52, 63, 73, 1);
        position: absolute;
        margin: 0;
        background-color: rgba(17, 24, 30, 1);
        width: 24px;
        height: 24px;
        text-align: center;
        vertical-align: middle;
        line-height: 24px;
    }

    .highlight-square-rect {
        pointer-events: none;
        fill: rgba(11, 18, 24, 0.6);
    }


</style>



// WEBPACK FOOTER //
// src/components/world-map/WorldMap.vue